/**
 * `appointment_delete` — the destructive half of the calendar surface.
 *
 * It lives beside `calendarWrites.js` for the reason `boardDeletes.js` lives
 * beside `boardWrites.js`: a delete fails differently. A tombstone reaches
 * every device the user owns and, in a team scope, every teammate, and
 * **nothing in this app un-deletes**.
 *
 * ## Three modes, and one of them is not a delete at all
 *
 * | the call | what happens |
 * |---|---|
 * | a single event, no `occurrence_date` | one tombstone |
 * | **one occurrence** of a series | the master's `exdates` gains that date and the MASTER IS RE-SAVED — an UPSERT, not a tombstone. If an override record already materialized that day it is *also* tombstoned, because otherwise the base instance RESURFACES (`deleteThisOccurrence`'s own documented two writes). |
 * | **the series**, no `occurrence_date` | the master is tombstoned and its override records are swept — and `cascade: true` is REQUIRED |
 *
 * The middle row is the inversion worth stating twice: **deleting one
 * occurrence of a series is an upsert.** Omitting the exdate while tombstoning
 * the override does not delete the occurrence — it un-edits it.
 *
 * ## O13/O14 — `cascade` and ordering
 *
 * `cascade: true` is required for a recurring MASTER, always, and never for a
 * single event or a single occurrence. That is O13's threshold read onto this
 * surface and it is what the app does: `requestDelete` raises the scope dialog
 * for a recurring target and goes straight to a plain confirm otherwise.
 * Deleting a series is never one thing (N occurrences, M override records, the
 * master); deleting one event or one occurrence always is.
 *
 * The refusal carries a LISTING of what would go, so the second call is
 * informed rather than blind. It is not a request for the USER to confirm —
 * the destructive switch is that (**O11**).
 *
 * **Children before the parent** (O14): the override records are tombstoned
 * FIRST and the master LAST. A failure part-way then leaves an over-full,
 * visible, still-deletable series rather than override records that are
 * present, synced, and reachable from nothing. `tombstoneInOrder` is a
 * sequential `for … await` and stops at the first failure; it must never become
 * `Promise.all`.
 *
 * ## Not atomic, and that is a cost, not a secret
 *
 * A series delete is N transactions and N outbox ops. The app's own
 * one-transaction primitive is `extension_records_sync_scope`, which hands a
 * stale array tombstones for every record absent from it — a verified data-loss
 * path — and it stays forbidden here. A partial failure comes back as
 * `isError: true` / `write_failed` carrying `deleted[]`, `failed[]` and
 * `complete: false`, never a cheerful success.
 */
import { invoke } from '@tauri-apps/api/core';
import { expandEvents } from '../../utils/calendarRecurrence';
import { fromDateKey, addDays, startOfDay } from '../../utils/calendarDates';
import { mcpError } from '../errors';
import { PERSONAL } from '../scopes';
import { scopeReport } from '../writeScope';
import {
  CALENDAR_EXTENSION_ID,
  MAX_WINDOW_DAYS,
  checkDateKey,
  hostTimezone,
  patchedEvent,
  recordForEvent,
} from '../calendarRecords';
import { casWriteEvent, loadForWrite, targetCalendarScope } from './calendarWrites';

async function tombstone(scopeKey, recordId) {
  await invoke('extension_records_delete', {
    workspaceId: scopeKey,
    extensionId: CALENDAR_EXTENSION_ID,
    recordId: String(recordId),
  });
}

/** Sequential, in the given order, stopping at the first failure. */
async function tombstoneInOrder(scopeKey, ids) {
  const deleted = [];
  for (const id of ids) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await tombstone(scopeKey, id);
      deleted.push(id);
    } catch {
      return { deleted, failed: ids.slice(deleted.length) };
    }
  }
  return { deleted, failed: [] };
}

/** How many occurrences the series produces in the next twelve months. */
function occurrencesNextYear(master) {
  const from = startOfDay(new Date());
  try {
    return expandEvents([master], from, addDays(from, MAX_WINDOW_DAYS)).length;
  } catch {
    return null;
  }
}

export async function appointmentDelete(args) {
  const wire = args.scope || PERSONAL;
  const target = targetCalendarScope(wire);
  if (target.error) return target.error;

  if (args.occurrence_date !== undefined && args.occurrence_date !== null) {
    const bad = checkDateKey(args.occurrence_date, 'occurrence_date');
    if (bad) return bad;
  }

  const { events } = await loadForWrite(target.scopeKey);
  const id = String(args.appointment_id);
  const master = events.find((e) => e.uid === id);
  if (!master) {
    return mcpError('not_found', 'No appointment with that id in this scope.');
  }
  const overrides = events.filter((e) => e.override_of?.master_uid === master.uid);
  const report = { scope: wire, timezone: hostTimezone(), ...scopeReport(wire) };

  // ── one occurrence: an UPSERT of the master's exdates, plus a tombstone of
  //    the override record if one exists ────────────────────────────────────
  if (args.occurrence_date) {
    if (!master.recurrence) {
      return mcpError(
        'invalid_parent',
        'That appointment does not repeat, so it has no occurrences. Delete it without occurrence_date.',
        { appointmentId: master.uid }
      );
    }
    const occurrenceDate = String(args.occurrence_date);
    if ((master.exdates || []).includes(occurrenceDate)) {
      return mcpError('not_found', 'That occurrence is already cancelled.', {
        appointmentId: master.uid,
        occurrenceDate,
      });
    }
    const day = fromDateKey(occurrenceDate);
    const generated = expandEvents([master], day, addDays(day, 1)).some(
      (i) => i.occurrence_date === occurrenceDate
    );
    const materialized = overrides.find(
      (o) => o.override_of.occurrence_date === occurrenceDate
    );
    if (!generated && !materialized) {
      return mcpError(
        'not_found',
        'That series has no occurrence on that date, so there is nothing to cancel.',
        { appointmentId: master.uid, occurrenceDate }
      );
    }

    // THE EXDATE FIRST. Without it, tombstoning the override merely un-edits
    // the occurrence: the base instance the override was suppressing comes
    // straight back.
    const next = patchedEvent(master, {
      exdates: [...(master.exdates || []), occurrenceDate],
    });
    // No `expected_rev`: a delete of a thing that has since been retitled is
    // still a delete of that thing, and refusing it would be the worse failure.
    const written = await casWriteEvent(target.scopeKey, recordForEvent(next), null);
    if (written.error) return written.error;

    const deleted = [];
    if (materialized) {
      try {
        await tombstone(target.scopeKey, materialized.uid);
        deleted.push(materialized.uid);
      } catch {
        return {
          ...mcpError(
            'write_failed',
            'The occurrence was cancelled but its edited copy could not be removed. Read the series again before doing anything else.',
            { appointmentId: master.uid, occurrenceDate }
          ),
          deleted,
          failed: [materialized.uid],
          complete: false,
        };
      }
    }
    return {
      appointmentId: master.uid,
      occurrenceDate,
      mode: 'occurrence',
      deleted,
      count: deleted.length,
      exdateAdded: true,
      complete: true,
      ...report,
    };
  }

  // ── the whole series ─────────────────────────────────────────────────────
  if (master.recurrence) {
    if (args.cascade !== true) {
      return mcpError(
        'cascade_required',
        'That is a repeating appointment, so deleting it removes every occurrence and every edited copy of one, permanently, on every device. Pass cascade: true to go ahead, or pass occurrence_date to cancel a single day instead.',
        {
          appointmentId: master.uid,
          occurrencesNext12Months: occurrencesNextYear(master),
          overrideRecords: overrides.length,
          exdates: (master.exdates || []).length,
        }
      );
    }
    // CHILDREN FIRST: the override records, then the master.
    const { deleted, failed } = await tombstoneInOrder(target.scopeKey, [
      ...overrides.map((o) => o.uid),
      master.uid,
    ]);
    if (failed.length) {
      return {
        ...mcpError(
          'write_failed',
          'The series was only partly deleted. Read the calendar again before doing anything else — do not simply retry.',
          { appointmentId: master.uid }
        ),
        deleted,
        failed,
        complete: false,
      };
    }
    return {
      appointmentId: master.uid,
      occurrenceDate: null,
      mode: 'series',
      deleted,
      count: deleted.length,
      overrideRecordsDeleted: overrides.length,
      complete: true,
      ...report,
    };
  }

  // ── a single event ───────────────────────────────────────────────────────
  const { deleted, failed } = await tombstoneInOrder(target.scopeKey, [master.uid]);
  if (failed.length) {
    return {
      ...mcpError('write_failed', 'The appointment could not be deleted.', {
        appointmentId: master.uid,
      }),
      deleted,
      failed,
      complete: false,
    };
  }
  return {
    appointmentId: master.uid,
    occurrenceDate: null,
    mode: 'single',
    deleted,
    count: deleted.length,
    complete: true,
    ...report,
  };
}
